import React, { useState } from "react";
import {
  UserGroupIcon,
  PlusIcon,
  ChevronDownIcon,
  CubeIcon,
  ScaleIcon,
  SparklesIcon,
  TagIcon,
} from "@heroicons/react/outline";
import Badge from "../../../../components/common/Badge";
import Button from "../../../../components/common/Button";

const ItemsTab = ({
  order,
  formatCurrency,
  itemsWithKarigarStatus,
  getKarigarStatusColor,
  onAssignKarigar,
  canAssign,
}) => {
  // Track which items have expanded breakdowns
  const [expandedItems, setExpandedItems] = useState({});

  const toggleBreakdown = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Calculate order summary
  const orderSummary = {
    totalItems: order.items?.length || 0,
    totalQuantity:
      order.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0,
    totalWeight:
      order.items?.reduce(
        (sum, item) => sum + (item.productSnapshot?.netWeight || 0),
        0
      ) || 0,
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Order Summary Header */}
      {order.items?.length > 0 && (
        <div className="flex items-center gap-2 sm:gap-4 px-1 py-2 text-xs sm:text-sm text-gray-500 border-b border-gray-100">
          <div className="flex items-center gap-1.5">
            <CubeIcon className="w-4 h-4" />
            <span>
              <span className="font-medium text-gray-700">
                {orderSummary.totalItems}
              </span>{" "}
              {orderSummary.totalItems === 1 ? "Item" : "Items"}
            </span>
          </div>
          <div className="w-px h-4 bg-gray-200" />
          <div className="flex items-center gap-1.5">
            <span>
              Qty:{" "}
              <span className="font-medium text-gray-700">
                {orderSummary.totalQuantity}
              </span>
            </span>
          </div>
          <div className="w-px h-4 bg-gray-200" />
          <div className="flex items-center gap-1.5">
            <ScaleIcon className="w-4 h-4" />
            <span>
              <span className="font-medium text-gray-700">
                {orderSummary.totalWeight.toFixed(2)}
              </span>
              g
            </span>
          </div>
        </div>
      )}

      {/* Items List */}
      {order.items?.map((item, index) => {
        const pricing = item.finalPricing || item.basePricing || {};
        const breakdown = pricing.breakdown || {};
        const charges = breakdown.charges || {};
        const gst = breakdown.gst || {};
        const karigarInfo = itemsWithKarigarStatus?.find(
          (i) => i.index === index
        )?.karigar;
        const isExpanded = expandedItems[index];
        const hasStones = item.productSnapshot?.stoneDetails?.length > 0;

        return (
          <div
            key={index}
            className="group border border-gray-200 rounded-xl sm:rounded-2xl overflow-hidden bg-white 
                       shadow-sm hover:shadow-md transition-shadow duration-200"
          >
            {/* Item Number Badge */}
            <div className="flex items-center justify-between px-3 sm:px-4 py-2 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-primary/10 text-primary text-xs sm:text-sm font-bold">
                  {index + 1}
                </span>
                <span className="text-xs text-gray-400 hidden sm:inline">
                  of {order.items.length}
                </span>
              </div>

              {/* Quick Tags */}
              <div className="flex items-center gap-1.5">
                {hasStones && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 text-[10px] sm:text-xs font-medium">
                    <SparklesIcon className="w-3 h-3" />
                    Stones
                  </span>
                )}
                {item.isCustomOrder && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[10px] sm:text-xs font-medium">
                    Custom
                  </span>
                )}
              </div>
            </div>

            {/* Main Content */}
            <div className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                {/* Product Image Placeholder + Info */}
                <div className="flex gap-3 flex-1 min-w-0">
                  {/* Product Thumbnail */}
                  <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg sm:rounded-xl bg-gradient-to-br from-gray-100 to-gray-50 border border-gray-200 flex items-center justify-center overflow-hidden">
                    {item.productSnapshot?.images?.[0] ? (
                      <img
                        src={item.productSnapshot.images[0]}
                        alt={item.productSnapshot?.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <CubeIcon className="w-8 h-8 text-gray-300" />
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 text-sm sm:text-base leading-tight mb-1.5 line-clamp-2">
                      {item.productSnapshot?.name ||
                        item.product?.name ||
                        "Unknown Product"}
                    </h4>

                    {/* SKU with copy indicator */}
                    <div className="flex items-center gap-1.5 mb-2">
                      <TagIcon className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs text-gray-500 font-mono">
                        {item.productSnapshot?.sku || "N/A"}
                      </span>
                    </div>

                    {/* Product Details - Compact Pills */}
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-[11px] sm:text-xs">
                        <span className="text-gray-400 mr-1">Qty:</span>
                        <span className="font-medium">
                          {item.quantity || 1}
                        </span>
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-[11px] sm:text-xs">
                        <span className="text-gray-400 mr-1">Wt:</span>
                        <span className="font-medium">
                          {item.productSnapshot?.netWeight || 0}g
                        </span>
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 text-[11px] sm:text-xs">
                        <span className="text-amber-500 mr-1">Purity:</span>
                        <span className="font-medium">
                          {item.productSnapshot?.metalDetails?.purity || "-"}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Pricing Section */}
                <div className="flex sm:flex-col items-end justify-between sm:justify-start gap-2 pt-3 sm:pt-0 border-t sm:border-t-0 border-gray-100 sm:min-w-[110px] lg:min-w-[130px]">
                  <div className="text-left sm:text-right">
                    <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wide">
                      Unit
                    </p>
                    <p className="text-sm sm:text-base font-semibold text-gray-700">
                      {formatCurrency(pricing.unitPrice || 0)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wide">
                      Total
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-primary">
                      {formatCurrency(pricing.totalPrice || 0)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Karigar Assignment */}
              {karigarInfo && (
                <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-gray-500">
                      <div className="p-1.5 rounded-lg bg-gray-100">
                        <UserGroupIcon className="w-4 h-4" />
                      </div>
                      <span className="text-xs sm:text-sm font-medium">
                        Karigar
                      </span>
                    </div>

                    {karigarInfo.isAssigned ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs sm:text-sm font-medium text-gray-900">
                          {karigarInfo.karigarName}
                        </span>
                        <Badge
                          variant={getKarigarStatusColor(karigarInfo.status)}
                          size="sm"
                          className="text-[10px] sm:text-xs"
                        >
                          {karigarInfo.status}
                        </Badge>
                      </div>
                    ) : canAssign ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          onAssignKarigar(index, item.product?._id)
                        }
                        className="text-amber-600 border-amber-300 hover:bg-amber-50 
                                   active:scale-95 transition-transform
                                   text-xs py-1.5 px-3 rounded-lg"
                      >
                        <PlusIcon className="w-3.5 h-3.5 mr-1" />
                        Assign Karigar
                      </Button>
                    ) : (
                      <Badge variant="yellow" size="sm">
                        Pending Assignment
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Price Breakdown - Expandable */}
            <div className="border-t border-gray-100">
              <button
                onClick={() => toggleBreakdown(index)}
                className="w-full flex items-center justify-between px-3 sm:px-4 py-2.5 
                           bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <span className="text-xs sm:text-sm font-medium text-gray-600">
                  Price Breakdown
                </span>
                <ChevronDownIcon
                  className={`w-4 h-4 text-gray-400 transition-transform duration-200 
                              ${isExpanded ? "rotate-180" : ""}`}
                />
              </button>

              {/* Expandable Content */}
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out
                            ${
                              isExpanded
                                ? "max-h-48 opacity-100"
                                : "max-h-0 opacity-0"
                            }`}
              >
                <div className="px-3 sm:px-4 py-3 bg-gray-50 border-t border-gray-100">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {/* Metal Value */}
                    <div className="bg-white rounded-lg p-2.5 border border-gray-100">
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-0.5">
                        Metal Value
                      </p>
                      <p className="text-sm sm:text-base font-semibold text-gray-800">
                        {formatCurrency(breakdown.metalValue || 0)}
                      </p>
                    </div>

                    {/* Making Charges */}
                    <div className="bg-white rounded-lg p-2.5 border border-gray-100">
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-0.5">
                        Making
                      </p>
                      <p className="text-sm sm:text-base font-semibold text-gray-800">
                        {formatCurrency(charges.making || 0)}
                      </p>
                    </div>

                    {/* Wastage */}
                    <div className="bg-white rounded-lg p-2.5 border border-gray-100">
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-0.5">
                        Wastage
                      </p>
                      <p className="text-sm sm:text-base font-semibold text-gray-800">
                        {formatCurrency(charges.wastage || 0)}
                      </p>
                    </div>

                    {/* GST */}
                    <div className="bg-white rounded-lg p-2.5 border border-gray-100">
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-0.5">
                        GST ({gst.rate || 3}%)
                      </p>
                      <p className="text-sm sm:text-base font-semibold text-gray-800">
                        {formatCurrency(gst.amount || 0)}
                      </p>
                    </div>
                  </div>

                  {/* Stone Charges if applicable */}
                  {charges.stones > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                      <span className="text-xs sm:text-sm text-gray-500">
                        Stone Charges
                      </span>
                      <span className="text-sm sm:text-base font-semibold text-gray-800">
                        {formatCurrency(charges.stones)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Empty State */}
      {(!order.items || order.items.length === 0) && (
        <div className="text-center py-12 sm:py-16">
          <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            <CubeIcon className="w-8 h-8 sm:w-10 sm:h-10 text-gray-300" />
          </div>
          <p className="text-sm sm:text-base text-gray-500 font-medium">
            No items in this order
          </p>
          <p className="text-xs sm:text-sm text-gray-400 mt-1">
            Items will appear here once added
          </p>
        </div>
      )}
    </div>
  );
};

export default ItemsTab;
